// 앱 키 저장소: 지원 탭이 띄운 iframe 이면 이 iframe 의 sessionStorage, 아니면 지금처럼 localStorage(tokenStore 가 정함)
import { getAccessToken, appStorage, setEmbeddedSupport, isEmbeddedSupport } from './tokenStore.js';

const CHANNEL = 'certilife-hospital-embed';
const TIMEOUT = 20000;
const SESSION_KEYS = [
    'accessTokenHos', 'refreshTokenHos', 'userNameHos', 'hospitalNameHos',
    'companyImageUrlHos', 'hospitalId', 'hospitalName', 'supportMsgChannel', 'localeHos', 'mfaTokenHos',
];

let parentOrigin = '';
let childId = '';
let sessionId = '';
let identity = null;
let accessToken = '';
let stopped = false;
let acknowledged = false;
let initialPromise = null;
let resolveInitial;
let rejectInitial;
let refresh = null;
let deadline;
let sessionCheck;
const endListeners = new Set();

export function isStudioEmbed() {
    return window.parent !== window;
}

function sameIdentity(a, b) {
    return !!a && !!b && a.companyId === b.companyId && a.accountId === b.accountId && a.role === b.role;
}

function decodeToken(token, allowExpired = false) {
    if (typeof token !== 'string') throw new Error('로그인 정보를 확인할 수 없습니다.');
    const parts = token.split('.');
    if (parts.length !== 3) throw new Error('로그인 정보를 확인할 수 없습니다.');
    const decoded = JSON.parse(decodeURIComponent(Array.from(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')),
        c => `%${c.charCodeAt(0).toString(16).padStart(2, '0')}`).join('')));
    const companyId = decoded.sub || decoded.companyId;
    if (decoded.role !== 'HOSPITAL' || typeof companyId !== 'string' || !companyId.trim() ||
        typeof decoded.accountId !== 'string' || !decoded.accountId.trim() ||
        typeof decoded.exp !== 'number' || !Number.isFinite(decoded.exp) ||
        (!allowExpired && decoded.exp * 1000 <= Date.now()) ||
        (decoded.sub && decoded.companyId && decoded.sub !== decoded.companyId)) {
        throw new Error('병원 계정의 로그인 정보가 유효하지 않습니다.');
    }
    // sid = 지원 세션 토큰(서버가 서명에 넣음) — studio 지원 탭이 넘긴 토큰이라는 표시
    return { identity: { companyId, accountId: decoded.accountId, role: 'HOSPITAL' }, name: decoded.name, support: !!decoded.sid };
}

function allowedParentOrigin() {
    const api = new URL(process.env.VUE_APP_SERVERURL || 'http://localhost:22133');
    const local = ['localhost', '127.0.0.1', '[::1]'].includes(api.hostname);
    const configured = process.env.VUE_APP_STUDIO_SERVER;
    const url = new URL(configured || (local ? `${api.protocol}//${api.hostname}:2214` : api.origin.replace('inapi', 'studio')));
    if (url.username || url.password || url.search || url.hash || url.pathname !== '/' ||
        (url.protocol !== 'https:' && !(url.protocol === 'http:' && ['localhost', '127.0.0.1', '[::1]'].includes(url.hostname))) ||
        (!configured && !local && !api.hostname.includes('inapi'))) {
        throw new Error('Studio 연결 주소를 확인해주세요.');
    }
    return url.origin;
}

// Studio 가 postMessage 로 넘긴 MFA 증표(URL 에는 싣지 않음, design.md §3)
function storeMfaToken(data) {
    if (typeof data.mfaToken === 'string' && data.mfaToken) appStorage().setItem('mfaTokenHos', data.mfaToken);
}

function send(type, extra = {}) {
    if (!parentOrigin || !childId) return;
    window.parent.postMessage({ channel: CHANNEL, version: 1, type, childId, ...(sessionId ? { sessionId } : {}), ...extra }, parentOrigin);
}

function clearStoredSession() {
    // A different hospital tab may have replaced the account already. Never erase it.
    const storage = appStorage();
    if (storage.getItem('accessTokenHos') !== accessToken) return;
    SESSION_KEYS.forEach(key => storage.removeItem(key));
}

export function endStudioSession(error = new Error('Studio에서 다시 로그인해주세요.'), notifyParent = true) {
    if (!isStudioEmbed() || stopped) return;
    if (notifyParent && sessionId) send('AUTH_ERROR', refresh ? { requestId: refresh.requestId } : {});
    stopped = true;
    window.clearTimeout(deadline);
    window.clearInterval(sessionCheck);
    window.removeEventListener('message', onMessage);
    window.removeEventListener('storage', checkStoredSession);
    window.removeEventListener('focus', checkStoredSession);
    clearStoredSession();
    accessToken = '';
    rejectInitial?.(error);
    refresh?.reject(error);
    refresh = null;
    endListeners.forEach(listener => listener(error));
}

function checkStoredSession() {
    if (!stopped && identity && appStorage().getItem('accessTokenHos') !== accessToken) {
        endStudioSession(new Error('병원 로그인 계정이 변경되었습니다. Studio에서 다시 로그인해주세요.'));
    }
}

// A token is accepted only from the configured Studio window. Studio verifies
// /auth/profile before INIT and every AUTH_UPDATED; the child also binds JWT claims
// to the original company/account so a cookie-account switch cannot be adopted.
function onMessage(event) {
    const data = event.data;
    if (stopped || event.source !== window.parent || event.origin !== parentOrigin ||
        !data || typeof data !== 'object' || data.channel !== CHANNEL || data.version !== 1 || data.childId !== childId) return;
    if (data.type === 'INIT') {
        if (sessionId || typeof data.sessionId !== 'string' || !data.sessionId || data.sessionId.length > 128) return;
        try {
            const parsed = decodeToken(data.accessToken);
            if (!sameIdentity(parsed.identity, data.identity)) throw new Error('병원 계정이 일치하지 않습니다.');
            sessionId = data.sessionId;
            identity = parsed.identity;
            // 지원 토큰이면 이 뒤로 앱 키는 이 iframe 의 sessionStorage 에만(같은 브라우저 일반 병원 로그인의 localStorage 는 건드리지 않음)
            setEmbeddedSupport(parsed.support);
            const storage = appStorage();
            SESSION_KEYS.forEach(key => storage.removeItem(key));
            accessToken = data.accessToken;
            storage.setItem('accessTokenHos', accessToken);
            storeMfaToken(data);
            if (typeof parsed.name === 'string') storage.setItem('userNameHos', parsed.name);
            window.clearTimeout(deadline);
            sessionCheck = window.setInterval(checkStoredSession, 1000);
            resolveInitial({ accessToken, identity: { ...identity } });
        } catch (error) {
            // Include the offered session so Studio can dismiss its loading state.
            sessionId = data.sessionId;
            endStudioSession(error);
        }
        return;
    }
    if (!sessionId || data.sessionId !== sessionId) return;
    if (data.type === 'SESSION_END') {
        endStudioSession(new Error('Studio 로그인이 종료되었습니다.'), false);
    } else if (data.type === 'AUTH_UPDATED') {
        if (!refresh || data.requestId !== refresh.requestId) return;
        try {
            const parsed = decodeToken(data.accessToken);
            if (!sameIdentity(identity, parsed.identity) || !sameIdentity(identity, data.identity) ||
                parsed.support !== isEmbeddedSupport()) throw new Error('병원 계정이 일치하지 않습니다.');
            getStudioAccessToken();
            accessToken = data.accessToken;
            appStorage().setItem('accessTokenHos', accessToken);
            storeMfaToken(data);
            window.clearTimeout(deadline);
            const pending = refresh;
            refresh = null;
            send('AUTH_OK', { requestId: data.requestId });
            pending.resolve(accessToken);
        } catch (error) {
            endStudioSession(error);
        }
    } else if (data.type === 'AUTH_ERROR') {
        if (data.requestId && data.requestId !== refresh?.requestId) return;
        endStudioSession(new Error('로그인 연결에 실패했습니다. Studio에서 다시 로그인해주세요.'), false);
    }
}

export function waitForStudioSession() {
    if (!isStudioEmbed()) return Promise.resolve(null);
    if (initialPromise) return initialPromise;
    initialPromise = new Promise((resolve, reject) => {
        resolveInitial = resolve;
        rejectInitial = reject;
        try {
            parentOrigin = allowedParentOrigin();
            childId = window.crypto.randomUUID();
            window.addEventListener('message', onMessage);
            window.addEventListener('storage', checkStoredSession);
            window.addEventListener('focus', checkStoredSession);
            deadline = window.setTimeout(() => endStudioSession(new Error('Studio에 연결할 수 없습니다. 인증서 메뉴를 다시 열어주세요.')), TIMEOUT);
            send('READY');
        } catch (error) {
            endStudioSession(error);
        }
    });
    return initialPromise;
}

export function getStudioAccessToken() {
    if (!isStudioEmbed()) return getAccessToken();
    checkStoredSession();
    if (stopped || !identity || !accessToken) throw new Error('Studio 로그인이 종료되었습니다.');
    return accessToken;
}

export function acknowledgeStudioSession() {
    if (!isStudioEmbed() || acknowledged) return;
    getStudioAccessToken();
    acknowledged = true;
    send('AUTH_OK');
}

export function requestStudioAccessToken() {
    if (!isStudioEmbed()) return Promise.reject(new Error('Studio 연결 화면이 아닙니다.'));
    try { getStudioAccessToken(); } catch (error) { return Promise.reject(error); }
    if (!acknowledged) return Promise.reject(new Error('병원 화면의 초기 인증에 실패했습니다. Studio에서 다시 로그인해주세요.'));
    if (refresh) return refresh.promise;
    const pending = { requestId: window.crypto.randomUUID() };
    pending.promise = new Promise((resolve, reject) => { pending.resolve = resolve; pending.reject = reject; });
    refresh = pending;
    deadline = window.setTimeout(() => endStudioSession(new Error('로그인 갱신 시간이 초과되었습니다. Studio에서 다시 로그인해주세요.')), TIMEOUT);
    send('AUTH_REQUIRED', { requestId: pending.requestId });
    return pending.promise;
}

export function requestStudioLogout() {
    if (!isStudioEmbed() || stopped) return;
    send('LOGOUT_REQUEST');
    endStudioSession(new Error('Studio 로그인이 종료되었습니다.'), false);
}

export function onStudioSessionEnd(listener) {
    endListeners.add(listener);
    return () => endListeners.delete(listener);
}
